/**
 * rabbit.js — Rabbit avatar: motion, mouth, eyes, speech
 *
 * initRabbit(isMutedFn) → { showRabbit, hideRabbit, speak, stopMouthAnimation, blinkEye }
 *
 * isMutedFn — pass the isMuted() function from sounds.js
 */

import gsap from 'gsap'

export function initRabbit(isMutedFn) {

    // ── DOM refs ────────────────────────────────────────────────
    const avatarContainer = document.getElementById('rabbit-avatar-container')
    const rabbitMouth     = document.getElementById('rabbit-mouth')
    const dust            = document.getElementById('magic-dust')

    // ── State ───────────────────────────────────────────────────
    let infinityTween = null
    let mouthInterval = null

    // ── Infinity (∞) animation ──────────────────────────────────
    function startInfinityAnimation() {
        if (!avatarContainer) return
        if (infinityTween) infinityTween.kill()

        gsap.set(avatarContainer, { xPercent: -50, yPercent: 0, x: 0, y: 0 })

        const params = { progress: 0 }
        const scale  = 100

        infinityTween = gsap.to(params, {
            progress: 1,
            duration: 18,
            repeat: -1,
            ease: 'none',
            onUpdate: () => {
                const t           = params.progress * Math.PI * 2
                const denominator = 1 + Math.pow(Math.cos(t), 2)
                const x = (scale * Math.sin(t)) / denominator
                const y = (scale * Math.sin(t) * Math.cos(t)) / denominator
                gsap.set(avatarContainer, { x, y })
            },
        })
    }

    function stopInfinityAnimation() {
        if (infinityTween) {
            infinityTween.kill()
            infinityTween = null
        }
        gsap.to(avatarContainer, { x: 0, y: 0, duration: 0.5 })
    }

    // ── Mouth animation (while speaking) ───────────────────────
    function startMouthAnimation() {
        clearInterval(mouthInterval)
        mouthInterval = setInterval(() => {
            if (!rabbitMouth) return
            rabbitMouth.src = rabbitMouth.src.includes('rabbit_open_eye_cloth_mouth')
                ? './rabbit_open_eye_open_mouth.webp'
                : './rabbit_open_eye_cloth_mouth.webp'
        }, 250)
        if (dust) dust.classList.add('active')
    }

    function stopMouthAnimation() {
        clearInterval(mouthInterval)
        mouthInterval = null
        if (rabbitMouth) rabbitMouth.src = './rabbit_open_eye_cloth_mouth.webp'
        if (dust) dust.classList.remove('active')
    }

    // ── Blink ───────────────────────────────────────────────────
    function blinkEye() {
        if (!rabbitMouth) return
        rabbitMouth.src = './rabbit_cloth_eye_cloth_mouth.webp'
        setTimeout(() => {
            if (rabbitMouth) rabbitMouth.src = './rabbit_open_eye_cloth_mouth.webp'
        }, 200)
    }

    // ── Speech ──────────────────────────────────────────────────
    const LANG_MAP = {
        uk: 'uk-UA', ru: 'ru-RU', en: 'en-US',
        es: 'es-ES',  fr: 'fr-FR', de: 'de-DE',
        it: 'it-IT',  pt: 'pt-PT', pl: 'pl-PL',
    }

    function speak(text, langCode) {
        if (isMutedFn()) return

        window.speechSynthesis.cancel()
        startMouthAnimation()

        const utterance   = new SpeechSynthesisUtterance(text)
        const targetLang  = LANG_MAP[langCode] || langCode || 'en-US'
        utterance.lang    = targetLang
        utterance.rate    = 0.9
        utterance.pitch   = 0.8

        // Pick best matching voice
        const voices = window.speechSynthesis.getVoices()
        const voice  = voices.find(v => v.lang === targetLang || v.lang.replace('_', '-') === targetLang)
                    || voices.find(v => v.lang.startsWith(targetLang.split('-')[0]))
        if (voice) utterance.voice = voice

        utterance.onend = () => {
            stopMouthAnimation()
            blinkEye()
            // Trigger onboarding register stage after first answer
            if (!localStorage.getItem('ob_done') && !localStorage.getItem('ob_register_shown')) {
                localStorage.setItem('ob_register_shown', '1')
                setTimeout(() => {
                    if (typeof window.obShowRegister === 'function') window.obShowRegister()
                }, 800)
            }
        }

        utterance.onerror = (e) => {
            if (e.error !== 'interrupted') console.error('SpeechSynthesis Error:', e)
            stopMouthAnimation()
        }

        window.speechSynthesis.speak(utterance)
    }

    // ── Show / Hide ─────────────────────────────────────────────
    function showRabbit() {
        if (!avatarContainer) return
        avatarContainer.style.display = 'block'
        // Double rAF for Safari
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                avatarContainer.classList.add('visible')
                if (dust) dust.classList.add('dust-burst')
                startInfinityAnimation()
            })
        })
        setTimeout(() => { if (dust) dust.classList.remove('dust-burst') }, 800)
    }

    function hideRabbit() {
        if (!avatarContainer?.classList.contains('visible')) return
        stopInfinityAnimation()
        stopMouthAnimation()
        if (dust) dust.classList.add('dust-burst')
        avatarContainer.classList.remove('visible')
        setTimeout(() => {
            avatarContainer.style.display = 'none'
            if (dust) dust.classList.remove('dust-burst')
            window.speechSynthesis.cancel()
        }, 800)
    }

    // Pre-load voices list so the first speak() call doesn't lag
    window.speechSynthesis.getVoices()
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices()
    }

    return { showRabbit, hideRabbit, speak, startMouthAnimation, stopMouthAnimation, blinkEye }
}
